import { Trans } from '@lingui/react/macro';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import SouthEastIcon from '@mui/icons-material/SouthEast';
import { Box, Tooltip, Typography } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import type { ViewPosition } from '@/feature/terminal_tables/position/types';
import { getBufferColor } from '@/utils/liquidationUtils';
import { ARROW_SX, formatLeverage, InfoRow, ValueWithProjection } from './helpers';
import type { MarginProjection } from './useMarginProjection';

interface MarginInfoRowsProps {
  selectedPosition: ViewPosition | null;
  formattedMaxAmount: string;
  projection: MarginProjection;
}

export function MarginInfoRows({ selectedPosition, formattedMaxAmount, projection }: MarginInfoRowsProps) {
  const { projectedValues, formattedCurrentMargin, formattedProjectedMargin, currentBuffer } = projection;

  return (
    <>
      <InfoRow label={<Trans comment="Adjust margin dialog: label for available margin row">Margin available to add</Trans>}>
        <Typography variant="body1" color="text.primary" className="numeric">
          {`$${formattedMaxAmount}`}
        </Typography>
      </InfoRow>

      <InfoRow label={<Trans comment="Adjust margin dialog: label for current margin row">Current margin</Trans>}>
        <ValueWithProjection
          current={selectedPosition ? `$${formattedCurrentMargin}` : ''}
          projected={projectedValues ? `$${formattedProjectedMargin}` : undefined}
          projectedDirection="up"
        />
      </InfoRow>

      <InfoRow label={<Trans comment="Adjust margin dialog: label for leverage row">Leverage</Trans>}>
        <ValueWithProjection
          current={selectedPosition ? `${formatLeverage(selectedPosition.leverage)}x` : ''}
          projected={projectedValues ? `${formatLeverage(projectedValues.newLeverage)}x` : undefined}
          projectedDirection="down"
        />
      </InfoRow>

      <InfoRow label={<Trans comment="Adjust margin dialog: label for liquidation level row">Liquidation level</Trans>}>
        {selectedPosition && selectedPosition.liquidationPrice > 0 && (
          <LiquidationLevelValue position={selectedPosition} projectedValues={projectedValues} currentBuffer={currentBuffer} />
        )}
      </InfoRow>
    </>
  );
}

function LiquidationLevelValue({
  position,
  projectedValues,
  currentBuffer,
}: {
  position: ViewPosition;
  projectedValues: MarginProjection['projectedValues'];
  currentBuffer: MarginProjection['currentBuffer'];
}) {
  const displayBuffer = projectedValues?.newLiqBuffer ?? currentBuffer;
  const bufferPct = displayBuffer != null ? Math.round(displayBuffer * 100) : null;
  const formatPrice = (price: number) => price.toLocaleString(undefined, { maximumFractionDigits: position.quoteDecimals });

  return (
    <>
      <Typography variant="body1" color="text.primary" className="numeric">
        {formatPrice(position.liquidationPrice)}
      </Typography>
      {projectedValues && (
        <>
          {projectedValues.newLiqPrice > position.liquidationPrice ? (
            <NorthEastIcon fontSize="small" sx={ARROW_SX} />
          ) : (
            <SouthEastIcon fontSize="small" sx={ARROW_SX} />
          )}
          <Typography variant="body1" color="text.primary" className="numeric">
            {formatPrice(projectedValues.newLiqPrice)}
          </Typography>
        </>
      )}
      {bufferPct != null && displayBuffer != null && <BufferTooltip bufferPct={bufferPct} displayBuffer={displayBuffer} />}
    </>
  );
}

function BufferTooltip({ bufferPct, displayBuffer }: { bufferPct: number; displayBuffer: number }) {
  return (
    <Tooltip
      arrow
      placement="bottom"
      title={
        <Box>
          <Typography variant="body2">
            <Trans comment="Adjust margin dialog: tooltip summary on the liquidation buffer percentage, side-neutral wording">
              You can survive a {{ pct: bufferPct }}% move before liquidation.
            </Trans>
          </Typography>
          <Typography variant="body2" color="warning.main">
            <Trans comment="Adjust margin dialog: tooltip threshold legend, risky band">{'<2% = Risky'}</Trans>
          </Typography>
          <Typography variant="body2" color="success.main">
            <Trans comment="Adjust margin dialog: tooltip threshold legend, safe band">{'>10% = Safe(ish)'}</Trans>
          </Typography>
        </Box>
      }
    >
      <Typography
        variant="body1"
        color={getBufferColor(displayBuffer)}
        className="numeric"
        sx={(theme: Theme) => ({
          ml: '2px',
          ...(theme.custom?.helpText ?? { cursor: 'help', borderBottom: '1px dashed currentColor' }),
        })}
      >
        {`(${bufferPct}% away)`}
      </Typography>
    </Tooltip>
  );
}
